/**
 * CertificateRow: a completion's certificate, as Home's "Recent" list and the
 * Records transcript show it (plan docs/plans/lms-plan.md 7.1, 7.5, 10.1,
 * 10.3, 10.4; R9, R24; WP-41).
 *
 * - A certificate: its code (LMS-YYYY-NNNNNN, 10.2) and Download. GET
 *   /certificates/:id/url answers a presigned URL (10 minutes, 10.4), never
 *   fetched into the page. The new tab is opened on the click itself, before
 *   the request (openPendingTab, the player's helper; plan 18.27: a popup
 *   opened after an awaited request can be blocked), its opener severed, and
 *   pointed at the URL when it arrives. A blocked tab leaves a visible link
 *   to the URL (target _blank, rel noopener noreferrer); a failed request
 *   closes the tab and says so in a toast.
 * - No certificate on a completion that earns one (10.3: the completion
 *   stands with certificate_id NULL and the daily pass retries): "Pending"
 *   with Retry (POST /completions/:id/certificate/retry). A 409
 *   LMS_NO_CERTIFICATE answer means this completion earns none after all
 *   (10.1), and the row says so.
 * - An external completion carries no module certificate (10.1: the uploaded
 *   file is the certificate); a best practices scenario issues none by
 *   default (10.1).
 *
 * The calls are WP-40's (api/completions.ts; plan 18.22).
 */
import { useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { apiErrorCode, apiErrorMessage } from '../api/client';
import { getCertificateUrl, retryCertificate } from '../api/completions';
import type { CertificateRef, CompletionKind } from '../api/completions';
import { openPendingTab } from './player/DoneStage';
import { outlineBtnCls } from '../../ui/styles';
import { useToast } from '../../ui/Toast';
import { Pill } from './StatusPill';

export interface CertificateRowProps {
  completionId: string;
  kind: CompletionKind | string;
  /** The course category (10.1: best_practices issues no certificate by default). */
  category?: string | null;
  certificate: CertificateRef | null;
  /** The course title, for the buttons' accessible names. */
  courseTitle?: string | null;
  /** Called with the certificate a retry produced. */
  onIssued?: (certificate: CertificateRef) => void;
}

type Missing = 'pending' | 'none' | 'external';

function missingReason(kind: string, category: string | null | undefined): Missing {
  if (kind === 'external') return 'external';
  if (category === 'best_practices') return 'none';
  return 'pending';
}

export function CertificateRow({ completionId, kind, category, certificate, courseTitle, onIssued }: CertificateRowProps) {
  const { toast } = useToast();
  const [cert, setCert] = useState<CertificateRef | null>(certificate);
  const [missing, setMissing] = useState<Missing>(() => missingReason(String(kind), category));
  const [downloading, setDownloading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const what = courseTitle ? ` for ${courseTitle}` : '';

  const download = async () => {
    if (!cert || downloading) return;
    // Before any await: opened inside the click's user activation (18.27).
    const tab = openPendingTab();
    setDownloading(true);
    setBlockedUrl(null);
    try {
      const { url } = await getCertificateUrl(cert.id, 'attachment');
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlockedUrl(url);
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      toast(apiErrorMessage(err, 'The certificate could not be downloaded. Try again.'), { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const out = await retryCertificate(completionId);
      if (out?.certificate) {
        const issued = { id: out.certificate.id, code: out.certificate.code };
        setCert(issued);
        onIssued?.(issued);
        toast('Your certificate is ready.', { type: 'success' });
      }
    } catch (err) {
      if (apiErrorCode(err) === 'LMS_NO_CERTIFICATE') {
        setMissing('none');
        toast('This training does not issue a certificate.', { type: 'info' });
      } else {
        toast(apiErrorMessage(err, 'The certificate is still pending. It is retried automatically every day.'), { type: 'error' });
      }
    } finally {
      setRetrying(false);
    }
  };

  if (cert) {
    return (
      <div className="flex flex-col gap-1" data-testid="lms-certificate-row">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-gray-900 dark:text-gray-100" data-testid="lms-certificate-code">
            {cert.code}
          </span>
          <button
            type="button"
            className={outlineBtnCls()}
            onClick={() => void download()}
            disabled={downloading}
            aria-label={`Download certificate ${cert.code}${what}`}
            data-testid="lms-certificate-download"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {downloading ? 'Preparing...' : 'Download'}
          </button>
        </div>
        {blockedUrl ? (
          <div className="text-xs text-amber-800 dark:text-amber-300" role="status" data-testid="lms-certificate-blocked">
            {'Your browser blocked the new tab. '}
            <a
              href={blockedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary-600 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
              data-testid="lms-certificate-link"
            >
              Open the certificate
            </a>
            {' (the link works for 10 minutes).'}
          </div>
        ) : null}
      </div>
    );
  }

  if (missing === 'external') {
    return (
      <span className="text-xs text-gray-600 dark:text-gray-400" data-testid="lms-certificate-row">
        Uploaded certificate
      </span>
    );
  }

  if (missing === 'none') {
    return (
      <span className="text-xs text-gray-600 dark:text-gray-400" data-testid="lms-certificate-row">
        No certificate
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="lms-certificate-row">
      <Pill tone="amber" data-testid="lms-certificate-pending">
        Certificate pending
      </Pill>
      <button
        type="button"
        className={outlineBtnCls()}
        onClick={() => void retry()}
        disabled={retrying}
        aria-label={`Retry the certificate${what}`}
        data-testid="lms-certificate-retry"
      >
        <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" />
        Retry
      </button>
    </div>
  );
}

export default CertificateRow;
